<template>
    <div class="container">
        <ContentOrLoading>
            <b-row v-if="zona_detalle">
                <LayoutSelectSeats
                    :zona="zona_detalle"
                    :disabled="loading"
                    @volver="$emit('volver')"
                >
                    <template v-slot:asientos-seleccionados>
                        <TusAsientosSGA
                            :loading="loading"
                            :tipo="tipo"
                            :asientos="asientos_seleccionados"
                            :zona="zona_detalle"
                            :max_asientos_seleccionados="max_asientos_seleccionados"
                            :max_seats="max_seats"
                            @deleteSeat="handleDeleteSeat"
                            @setUltimoPrecio="(lastPrice) => { ultimo_precio_seleccinado = lastPrice }"
                            @setMaxAsientos="(max) => { max_asientos_seleccionados = max }"
                            @continuar="continuar"
                        />
                    </template>
                    <template v-slot:seat-map>
                        <SeatMapSga
                            :loading="loading"
                            :print_align="zona_detalle.print_align"
                            :filas="zona_detalle.configuracion"
                            :max_asientos_seleccionados="max_asientos_seleccionados"
                            @asientoSeleccionado="selectSeat"
                        />
                    </template>
                </LayoutSelectSeats>
                <button type="button" name="button" class="button effect-button mr-4 mt-4 ml-auto" :class="{ 'large-button': !esMovil }" :disabled="loading" @click="$emit('volver')">
                    <span>{{ $t('formularios.atras') }}</span>
                </button>
            </b-row>
        </ContentOrLoading>
    </div>
</template>

<script>
    import SeatMapSga from '../../../common/Asientos/SeatMapSga.vue';
    import LayoutSelectSeats from '../../../common/Asientos/LayoutSelectSeats.vue';
    import TusAsientosSGA from './components/TusAsientosSGA.vue';

    export default {
        props: {
            zona: {
                type: Object,
                required: true
            },
            sga_event_id: {
                type: String,
                required: true
            },
            event_session_id: {
                type: Number,
                required: false
            },
            tipo: {
                type: String,
                required: true
            },
            isPrivate: {
                type: Boolean,
                default: false
            },
            buyerId: {
                type: String,
                default: null
            },
            licensorEntitlementId: {
                type: String,
                default: null
            }
        },
        data() {
            return {
                zona_detalle: null,
                max_seats: 10,
                asientos_seleccionados: [],
                loading: false,
                max_asientos_seleccionados: false,
                categoriasAvisadas: new Set(),
                ultimo_precio_seleccinado: null
            }
        },
        created() {
            this.loadZona(this.zona.id);
        },
        methods: {
            loadZona(id) {
                this.loading = true;

                const data = {
                    zona_id: id,
                    event_id: this.sga_event_id,
                    filters: {
                        group: this.tipo != 'eventos' ? 1 : 0,
                        ...(this.isPrivate && { isPrivate: true })
                    }
                };

                this.$store.dispatch(this.config.getVuexAlias('sga.zonas.asientos'), data).then(res => {
                    this.zona_detalle = res.data.data;
                }).finally(() => this.loading = false);
            },
            selectSeat(asiento, filaAlias) {
                asiento.zona_nombre = this.zona_detalle.name;
                // Ocultamos precios sin nombre (name null) que devuelve el SGA
                // para esta zona. Evita que aparezca "null - XX €" en el selector.
                asiento.precios = this.zona_detalle.precios.filter(p => p.zoneId === asiento.zonaComercialId && p.name);

                // La fila llega en el payload del evento: SeatMapSga ya no la
                // escribe sobre el asiento, así que aquí se guarda sin mutar el
                // objeto compartido más allá de lo que este flujo necesita.
                if (filaAlias != null) {
                    asiento.fila = filaAlias;
                }

                const yaSeleccionado = this.asientos_seleccionados.some(s => s.seat_id === asiento.seat_id);

                if (yaSeleccionado) {
                    this.asientos_seleccionados = this.asientos_seleccionados.filter(
                        a => a.seat_id !== asiento.seat_id
                    );
                    this.toggleSeatInMap(asiento.seat_id, false);
                    this.max_asientos_seleccionados = this.anyCategoryAtLimit();
                    return;
                }

                if (asiento.ocupado) {
                    this.$toast.error("Asiento ocupado");
                    return;
                }

                const channelLimit = this.getChannelLimit();

                let limiteSuperado = false;

                Object.entries(channelLimit).forEach(([productId, limit]) => {
                    let asientos = this.asientos_seleccionados.filter(a => a.precio_seleccionado?.product_id == productId);

                    if (asientos.length >= limit && limit != 0) {
                        this.$toast.error("Has superado el máximo de asientos permitido por canal de venta.");
                        limiteSuperado = true;
                        return;
                    }
                });

                if (limiteSuperado) return;

                let candidatePrice = asiento.precio_seleccionado ?? (asiento.precios.length ? asiento.precios[0] : null);

                if (this.ultimo_precio_seleccinado) {
                    candidatePrice = this.ultimo_precio_seleccinado;
                }

                const candidateCatId = candidatePrice?.salesCategoryId ?? null;
                const limiteCategoria = this.getCategoryLimit(candidateCatId);
                const conteoActual = this.countSelectedByCategory(this.asientos_seleccionados, candidateCatId);

                if (candidateCatId && conteoActual >= limiteCategoria) {
                    if (asiento.precio_seleccionado) {
                        this.$set(asiento, "precio_seleccionado", null);
                    }

                    this.asientos_seleccionados.push(asiento);
                    this.toggleSeatInMap(asiento.seat_id, true, this.ultimo_precio_seleccinado);

                    if (!this.categoriasAvisadas.has(candidateCatId)) {
                        this.$toast.error('Has superado el máximo de ' + limiteCategoria + ' asientos para esta categoría. El asiento se añadirá sin precio.');
                        this.categoriasAvisadas.add(candidateCatId);
                        this.$set(asiento, "precio_seleccionado", null);
                    }

                    this.max_asientos_seleccionados = this.anyCategoryAtLimit();
                    return;
                }

                if (!asiento.precio_seleccionado && asiento.precios.length) {
                    if (this.ultimo_precio_seleccinado) {
                        this.$set(asiento, "precio_seleccionado", this.ultimo_precio_seleccinado);
                    } else {
                        this.$set(asiento, "precio_seleccionado", asiento.precios[0]);
                    }
                }

                this.asientos_seleccionados.push(asiento);
                this.toggleSeatInMap(asiento.seat_id, true, asiento.precio_seleccionado ?? null);

                this.max_asientos_seleccionados = this.anyCategoryAtLimit();
            },
            continuar() {
                this.loading = true;

                let data = {
                    'salesChannelId': this.teatro.sga_sale_channel_id,
                    'tipo': this.tipo,
                    'cartItems': []
                };

                this.asientos_seleccionados.forEach(asiento => {
                    let productInCart = data.cartItems.find(item => item.cartItem?.productId == asiento.precio_seleccionado.product_id);


                    if (!productInCart) {
                        productInCart = {
                            'cartItem': {
                                'productId': asiento.precio_seleccionado.product_id,
                                'seats': []
                            }
                        };
                        data.cartItems.push(productInCart);
                    }

                    productInCart.cartItem.seats.push({
                        'id': asiento.seat_id,
                        'row': asiento.fila,
                        'seat': asiento.asiento,
                        'salesCategoryId': asiento.precio_seleccionado.salesCategoryId,
                        'precio_id': asiento.precio_seleccionado.id,
                        'zoneId': this.zona.sga_id,
                    });
                });             

                if(this.event_session_id) {
                    data.event_session_id = this.event_session_id;
                }
                
                const cookie = this.$cookies.get('comunicados-track-id');

                if(cookie) {
                    data.token_comunicado = cookie;
                }

                const fbp = this.$cookies.get("_fbp");
                const fbc = this.$cookies.get("_fbc");

                if (fbp && fbc){
                    data.fbp = fbp;
                    data.fbc = fbc;
                }

                // PRIVATE FLOW: Add buyer_id and licensor_entitlement_id if both are truthy
                const hasPrivateContext = this.buyerId && this.licensorEntitlementId;
                if (this.isPrivate && !hasPrivateContext) {
                    console.warn('[SGA Cart] Private flow missing licensorEntitlementId — skipping private fields');
                }
                
                // Conditionally inject private fields into payload
                if (hasPrivateContext) {
                    data.buyer_id = this.buyerId;
                    data.licensor_entitlement_id = this.licensorEntitlementId;
                }

                this.$store.dispatch(this.config.getVuexAlias('sga.cart.create'), data).then(res => {
                    if (this.tipo === 'eventos') {
                        localStorage.setItem('sga_eventos_cart_id', res.data.cart_id);
                    } else {
                        localStorage.setItem('sga_abono_cart_id', res.data.cart_id);
                    }
                    this.$emit('sesionCreada', res.data.items, res.data.cart_id);
                }).finally(() => this.loading = false);
            },
            getCategoryLimit(catId) {
                Object.values(this.zona_detalle?.configuration || {}).forEach(product => {
                    const categoria = product.salesCategories.find(
                        c => c.salesCategoryId === catId
                    );

                    if(categoria) return categoria?.upperLimit ?? 0;
                });
            },
            getSelectedSeats() {
                const seleccionados = [];

                Object.values(this.zona_detalle.configuracion).forEach(fila => {
                    Object.values(fila.asientos).forEach(a => {
                        if (a.seleccionado) {
                            seleccionados.push({
                                ...a,
                                precio_seleccionado: a.precio_seleccionado ?? null
                            });
                        }
                    });
                });

                return seleccionados;
            },
            countSelectedByCategory(seleccionados, catId) {
                if (!catId) return 0;
                return seleccionados.filter(
                    s => s.precio_seleccionado?.salesCategoryId === catId
                ).length;
            },
            anyCategoryAtLimit() {
                if (!this.zona_detalle?.configuration?.salesCategories?.length) return false;
                return this.zona_detalle.configuration.salesCategories.some(cat =>
                    this.countSelectedByCategory(this.asientos_seleccionados, cat.salesCategoryId) >= cat.upperLimit
                );
            },
            toggleSeatInMap(seatId, selected, precioSeleccionado) {
                Object.values(this.zona_detalle.configuracion).forEach(fila => {
                    Object.values(fila.asientos).forEach(a => {
                        if (a.seat_id === seatId) {
                            this.$set(a, "seleccionado", selected);
                            if (selected && precioSeleccionado !== undefined) {
                                this.$set(a, "precio_seleccionado", precioSeleccionado);
                            }
                        }
                    });
                });
            },
            getChannelLimit() {
                let limits = {};

                const configuration = this.zona_detalle?.configuration || {};

                Object.keys(configuration).forEach(productId => {
                    const fc = configuration[productId];

                    if (fc.salesChannels?.length) {
                        limits[productId] = fc.salesChannels.find(
                            sc => sc.salesChannelId === this.teatro.sga_sale_channel_id
                        )?.upperLimit ?? 0;
                    }
                });

                return limits;
            },
            handleDeleteSeat(seat_id, selected) {
                const index = this.asientos_seleccionados.findIndex(s => s.seat_id === seat_id);
                if (index !== -1) {
                    this.asientos_seleccionados.splice(index, 1);
                    this.toggleSeatInMap(seat_id, selected);
                }
            },
        },
        components: {
            SeatMapSga, LayoutSelectSeats, TusAsientosSGA
        },
        computed: {
            esMovil() {
                return window.innerWidth <= 768;
            },
            lista() {
                return this.$store.getters.getListaAbonados;
            },

            teatro(){
                return this.$store.getters.getTeatro;
            }
        },
    }
</script>